import React from 'react';
import { useTranslation } from 'react-i18next';

import { Tiles3tzModel } from '../../../../../../viewer/geodata/3d-tiled-models/tiles-3tz/tiles-3tz-model';
import { useOptionalUserState } from '../../../../../state/user-state/user-state';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Slider } from '../../../../slider/slider';

/**
 * How much detail a 3TZ tiled model loads and how much of it is kept, for organization admins to
 * tune, and how many of its levels load, to compare them close up. The settings hold for every 3TZ
 * in the session.
 */
const TiledModelSettings = () => {
  const { t } = useTranslation();
  const { settings } = Tiles3tzModel;
  const [errorTarget, setErrorTarget] = React.useState(settings.errorTarget);
  const [memoryBudgetMb, setMemoryBudgetMb] = React.useState(settings.memoryBudgetMb);
  const [maxLevel, setMaxLevel] = React.useState(settings.maxLevel);
  // Shared views may have no user, who then isn't an organization admin
  const isOrganizationAdmin = useOptionalUserState()?.isOrganizationAdmin ?? false;

  const levels = Tiles3tzModel.current?.availableLevels;
  if (!isOrganizationAdmin || levels === undefined) {
    return null;
  }

  const onErrorTarget = (pixels: number) => {
    Tiles3tzModel.settings.errorTarget = pixels;
    setErrorTarget(pixels);
  };

  const onMemoryBudget = (megabytes: number) => {
    Tiles3tzModel.settings.memoryBudgetMb = megabytes;
    setMemoryBudgetMb(megabytes);
  };

  /** @param count The levels to load, 1 for only the root's. All of them lifts the limit. */
  const onLevels = (count: number) => {
    const level = count < levels ? count - 1 : undefined;
    Tiles3tzModel.settings.maxLevel = level;
    setMaxLevel(level);
  };
  const levelCount = Math.min(maxLevel ?? levels, levels - 1) + 1;

  return (
    <>
      <LabelledContainer
        noWrap={true}
        text={t('viewSettings.tiledModel.detail.title', {
          ns: 'skyviewToolbar',
          value: errorTarget,
        })}
      >
        <Slider max={64} min={4} step={4} value={errorTarget} onChange={onErrorTarget} />
      </LabelledContainer>
      <LabelledContainer
        noWrap={true}
        text={t('viewSettings.tiledModel.memoryBudget.title', {
          ns: 'skyviewToolbar',
          value: memoryBudgetMb,
        })}
      >
        <Slider max={4096} min={256} step={128} value={memoryBudgetMb} onChange={onMemoryBudget} />
      </LabelledContainer>
      <LabelledContainer
        noWrap={true}
        text={t('viewSettings.tiledModel.levels.title', {
          ns: 'skyviewToolbar',
          value: levelCount,
          levels,
        })}
      >
        <Slider max={levels} min={1} step={1} value={levelCount} onChange={onLevels} />
      </LabelledContainer>
    </>
  );
};

export { TiledModelSettings };
